/**
 * portalClient — axios instance for the admin portal.
 *
 * Separate from the main `api_instance` on purpose: the portal token has no
 * refresh flow, so the silent-refresh interceptors there don't apply. Here a
 * 401 on any authenticated call means the session is over — clear it and
 * return to /portal/login.
 */

import axios, { AxiosError } from "axios";

import { usePortalAuthStore } from "../store/portalAuthStore";

const portalClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
    ClientDb: process.env.REACT_APP_CLIENT_DB || "",
  },
  timeout: 30_000,
});

// Attach the bearer token when a session exists.
portalClient.interceptors.request.use((config) => {
  const token = usePortalAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  return config;
});

// Expired/invalid session → clear and re-login. A 401 from the login call
// itself (no bearer attached) is a wrong password, not a dead session —
// let the form handle it.
portalClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const hadToken = !!error.config?.headers?.Authorization;
    if (error.response?.status === 401 && hadToken) {
      usePortalAuthStore.getState().clearSession();
      window.location.assign("/portal/login");
    }

    const data = error.response?.data as Record<string, unknown> | undefined;
    if (data?.detail) {
      if (typeof data.detail === "string") {
        error.message = data.detail;
      } else if (Array.isArray(data.detail)) {
        const msgs = data.detail
          .map((d: { msg?: string }) => d.msg)
          .filter(Boolean);
        if (msgs.length) error.message = msgs.join("; ");
      }
    }

    return Promise.reject(error);
  },
);

export default portalClient;
